import { useState } from "react";
import { BadgeCheck, ShieldCheck, Leaf, Award } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { CERTIFICATES } from "@/data/catalog";

const STRIP_ITEMS = [
  "ISO 9001:2015 Certified",
  "WHO-GMP Certified",
  "ISO 22000:2018 Food Safety",
  "FSSAI Central Licensed",
  "100% Herbal Ayurvedic",
  "No Harmful Chemicals",
  "Made in Jaipur, India",
  "Ships All Over India",
];

export const TrustStrip = () => {
  const items = [...STRIP_ITEMS, ...STRIP_ITEMS];
  return (
    <div data-testid="trust-strip" className="bg-[#1A4331] overflow-hidden py-3 border-y border-[#D4AF37]/20">
      <div className="flex whitespace-nowrap animate-marquee w-max">
        {items.map((t, i) => (
          <span key={i} className="flex items-center text-[#F9F6F0]/90 text-sm font-medium px-6">
            <Leaf className="w-3.5 h-3.5 mr-2 text-[#D4AF37]" />
            {t}
          </span>
        ))}
      </div>
    </div>
  );
};

export const Certifications = () => {
  const [openCert, setOpenCert] = useState(null);

  return (
    <section data-testid="certifications-section" id="certifications" className="py-16 sm:py-24 bg-[#F9F6F0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C05C46] mb-3">
            Certified Quality
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1c2826] mb-4">
            Certifications You Can Verify
          </h2>
          <p className="text-base text-[#4B5563] leading-relaxed">
            Every Eleven Stars product is manufactured at a certified facility — independently
            assessed for quality, food safety and good manufacturing practice.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {CERTIFICATES.map((c) => (
            <button
              key={c.title}
              data-testid={`cert-card-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setOpenCert(c)}
              className="group text-left bg-white rounded-xl border border-[#E5E0D8] shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden"
            >
              <div className="aspect-[3/4] bg-white p-3 overflow-hidden">
                <img
                  src={c.image}
                  alt={c.title}
                  className="w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <div className="p-4 border-t border-[#E5E0D8]">
                <div className="flex items-center gap-2 mb-1">
                  <Award className="w-4 h-4 text-[#D4AF37]" />
                  <span className="font-semibold text-[#1c2826]">{c.title}</span>
                </div>
                <p className="text-sm text-[#4B5563]">{c.subtitle}</p>
                <p className="text-xs text-[#4B5563]/70 mt-1">{c.issuer}</p>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[#1A4331]">
          <span className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#C05C46]" /> Independently audited facility
          </span>
          <span className="flex items-center gap-2">
            <BadgeCheck className="w-4 h-4 text-[#C05C46]" /> FSSAI License No. 12222999000568
          </span>
          <span className="flex items-center gap-2">
            <Leaf className="w-4 h-4 text-[#C05C46]" /> Herbal Ayurvedic manufacturer
          </span>
        </div>
      </div>

      <Dialog open={!!openCert} onOpenChange={() => setOpenCert(null)}>
        <DialogContent data-testid="cert-lightbox" className="max-w-2xl p-2 bg-white">
          <DialogTitle className="sr-only">{openCert?.title}</DialogTitle>
          {openCert && (
            <img src={openCert.image} alt={openCert.title} className="w-full h-auto rounded-lg" />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
